import {
  Assignment as AssignmentIcon,
  FilterList as FilterListIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TablePagination,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import empreendimentoService from "../services/empreendimento";
import licencasService from "../services/licencas";
import { formatDateToShow } from "../utils/formatters";
import { mascaraStatusEmpreendedor } from "../utils/constants";
import StatusChip from "../components/StatusChip";

function MinhasSolicitacoes() {
  const [licencas, setLicencas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFiltros, setLoadingFiltros] = useState(false);
  const [error, setError] = useState("");
  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [filtrosForm, setFiltrosForm] = useState({
    tipoLicencaId: "",
    empreendimentoId: "",
    dataInicio: "",
    dataFim: "",
  });
  const [filtrosAplicados, setFiltrosAplicados] = useState({
    tipoLicencaId: "",
    empreendimentoId: "",
    dataInicio: "",
    dataFim: "",
  });
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [itensPorPagina, setItensPorPagina] = useState(10);
  const [totalSolicitacoes, setTotalSolicitacoes] = useState(0);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    carregarOpcoesFiltros();
  }, []);

  useEffect(() => {
    carregarLicencas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosAplicados, paginaAtual, itensPorPagina]);

  const carregarOpcoesFiltros = async () => {
    try {
      setLoadingFiltros(true);

      const [tiposResponse, empreendimentosResponse] = await Promise.all([
        api.get("TipoLicenca/GetByNomeAsync", { params: { nome: "" } }),
        empreendimentoService.listarPaginado({}, 1, 100),
      ]);

      setTiposLicenca(
        Array.isArray(tiposResponse?.data) ? tiposResponse.data : [],
      );
      setEmpreendimentos(empreendimentosResponse?.items || []);
    } catch {
      setTiposLicenca([]);
      setEmpreendimentos([]);
    } finally {
      setLoadingFiltros(false);
    }
  };

  const carregarLicencas = async () => {
    try {
      setLoading(true);
      setError("");

      const resultado = await licencasService.listarMinhasSolicitacoesPaginado({
        ...filtrosAplicados,
        pageNumber: paginaAtual,
        pageSize: itensPorPagina,
      });

      setLicencas(resultado.items || []);
      setTotalSolicitacoes(resultado.totalCount || 0);
    } catch (err) {
      setError("Erro ao carregar licenças");
      setLicencas([]);
      setTotalSolicitacoes(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltrar = () => {
    if (
      filtrosForm.dataInicio &&
      filtrosForm.dataFim &&
      filtrosForm.dataInicio > filtrosForm.dataFim
    ) {
      setError("A data inicial não pode ser maior que a data final.");
      return;
    }

    setError("");
    setPaginaAtual(1);
    setFiltrosAplicados({ ...filtrosForm });
  };

  const handleLimparFiltros = () => {
    const filtrosLimpos = {
      tipoLicencaId: "",
      empreendimentoId: "",
      dataInicio: "",
      dataFim: "",
    };
    setFiltrosForm(filtrosLimpos);
    setPaginaAtual(1);
    setFiltrosAplicados(filtrosLimpos);
  };

  const getTipoLicencaLabel = (tipo) => {
    const tipoLabels = {
      PREVIA: "Licença Prévia",
      INSTALACAO: "Licença de Instalação",
      OPERACAO: "Licença de Operação",
      LAC: "Licença por Adesão e Compromisso",
      LAU: "Licença Ambiental Única",
    };
    return tipoLabels[tipo] || tipo;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mt: 4, mb: 8 }}>
        {/* Cabeçalho */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <AssignmentIcon
              sx={{
                fontSize: { xs: 32, md: 40 },
                color: theme.palette.primary.main,
                mr: 2,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.1rem", md: "1.5rem" },
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Minhas Licenças
            </Typography>
          </Box>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/nova-solicitacao")}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            Nova Solicitação
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Grid container spacing={2} alignItems="end">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" disabled={loadingFiltros}>
                <InputLabel id="tipo-licenca-filtro-label">
                  Tipo de Licença
                </InputLabel>
                <Select
                  labelId="tipo-licenca-filtro-label"
                  label="Tipo de Licença"
                  value={filtrosForm.tipoLicencaId}
                  onChange={(e) =>
                    setFiltrosForm((prev) => ({
                      ...prev,
                      tipoLicencaId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  {tiposLicenca.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>
                      {tipo.nome || tipo.codigo || "Tipo de licença"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small" disabled={loadingFiltros}>
                <InputLabel id="empreendimento-filtro-label">
                  Empreendimento
                </InputLabel>
                <Select
                  labelId="empreendimento-filtro-label"
                  label="Empreendimento"
                  value={filtrosForm.empreendimentoId}
                  onChange={(e) =>
                    setFiltrosForm((prev) => ({
                      ...prev,
                      empreendimentoId: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="">Todos</MenuItem>
                  {empreendimentos.map((empreendimento) => (
                    <MenuItem key={empreendimento.id} value={empreendimento.id}>
                      {empreendimento.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data inicial"
                type="date"
                value={filtrosForm.dataInicio}
                onChange={(e) =>
                  setFiltrosForm((prev) => ({
                    ...prev,
                    dataInicio: e.target.value,
                  }))
                }
                inputProps={{ max: filtrosForm.dataFim || undefined }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Data final"
                type="date"
                value={filtrosForm.dataFim}
                onChange={(e) =>
                  setFiltrosForm((prev) => ({
                    ...prev,
                    dataFim: e.target.value,
                  }))
                }
                inputProps={{ min: filtrosForm.dataInicio || undefined }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ justifyContent: { md: "flex-end" } }}
              >
                <Button
                  variant="contained"
                  startIcon={<FilterListIcon />}
                  onClick={handleFiltrar}
                  fullWidth
                  sx={{
                    minWidth: { sm: 110 },
                    textTransform: "none",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Filtrar
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleLimparFiltros}
                  fullWidth
                  sx={{
                    minWidth: { sm: 110 },
                    textTransform: "none",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Limpar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={3}>
          {licencas.length === 0 ? (
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  textAlign: "center",
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                  border: `1px dashed ${alpha(
                    theme.palette.primary.main,
                    0.2
                  )}`,
                }}
              >
                <Typography color="textSecondary">
                  Nenhuma licença encontrada
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate("/nova-solicitacao")}
                  sx={{ mt: 2, borderRadius: 2, textTransform: "none" }}
                >
                  Solicitar Nova Licença
                </Button>
              </Paper>
            </Grid>
          ) : (
            licencas.map((licenca) => (
              <Grid item xs={12} key={licenca.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                    "&:hover": {
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={3}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {getTipoLicencaLabel(licenca.nomeTipoLicenca)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body2" color="textSecondary">
                          Data da Solicitação:
                        </Typography>
                        <Typography>
                          {formatDateToShow(licenca.createAt)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        <Typography variant="body2" color="textSecondary">
                          Empreendimento:
                        </Typography>
                        <Typography>
                          {licenca.empreendimento?.nome || "Não informado"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <StatusChip
                          status={mascaraStatusEmpreendedor(licenca.status)}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={2}
                        sx={{ textAlign: { xs: "left", sm: "right" } }}
                      >
                        <IconButton
                          aria-label="Ver solicitação"
                          onClick={() => navigate(`/solicitacao/${licenca.id}`)}
                          sx={{
                            color: theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                            },
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>

        <Box sx={{ overflowX: "auto", width: "100%" }}>
        <TablePagination
          component="div"
          count={totalSolicitacoes}
          page={Math.max(0, paginaAtual - 1)}
          onPageChange={(_, newPage) => setPaginaAtual(newPage + 1)}
          rowsPerPage={itensPorPagina}
          onRowsPerPageChange={(event) => {
            setItensPorPagina(parseInt(event.target.value, 10));
            setPaginaAtual(1);
          }}
          rowsPerPageOptions={[5, 10, 20, 50]}
          labelRowsPerPage="Itens por página"
        />
        </Box>
      </Box>
    </Container>
  );
}

export default MinhasSolicitacoes;
